import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { finalize } from 'rxjs';
import { SommelierService } from '@proxy/sommeliers';
import { ClientSommelierByUserDto } from '@proxy/sommeliers/models';
import { CatalogService } from '@proxy/catalogs';
import { IdentityRoleSwitchService } from '@proxy/identity';
import { SommelierPublicDto } from '@proxy/catalogs/models';
import { NotificationService } from '../shared/services/notification.service';
import { StorageService } from '../shared/services/storage.service';
import { ConfirmationModalService } from '../shared/services/confirmation-modal.service';

@Component({
  selector: 'app-admin-client-sommelier',
  templateUrl: './admin-client-sommelier.component.html',
  standalone: false,
})
export class AdminClientSommelierComponent implements OnInit, OnDestroy {
  userId = '';
  info: ClientSommelierByUserDto | null = null;
  currentPhotoUrl: string | null = null;
  isLoading = true;

  showModal = false;
  keyword = '';
  province: string | null = null;
  locality: string | null = null;
  results: SommelierPublicDto[] = [];
  resultPhotos: Record<number, string> = {};
  isSearching = false;
  searchError = false;
  selected: SommelierPublicDto | null = null;
  isSubmitting = false;

  provinceOptions: { value: string; label: string }[] = [];
  localityOptions: { value: string; label: string }[] = [];

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private sommelierService: SommelierService,
    private catalogService: CatalogService,
    private roleSwitchService: IdentityRoleSwitchService,
    private not: NotificationService,
    private storageService: StorageService,
    private confirmation: ConfirmationModalService,
  ) {}

  ngOnInit(): void {
    this.userId = this.route.snapshot.paramMap.get('userId') || '';
    this.load();
  }

  ngOnDestroy(): void {
    this.revokeCurrentPhoto();
    this.revokeResultPhotos();
  }

  private revokeCurrentPhoto(): void {
    if (this.currentPhotoUrl) {
      URL.revokeObjectURL(this.currentPhotoUrl);
      this.currentPhotoUrl = null;
    }
  }

  private revokeResultPhotos(): void {
    Object.values(this.resultPhotos).forEach(url => URL.revokeObjectURL(url));
    this.resultPhotos = {};
  }

  goBack(): void {
    this.location.back();
  }

  initials(name?: string): string {
    const parts = (name || '').trim().split(/\s+/);
    return `${parts[0]?.charAt(0) || ''}${parts[1]?.charAt(0) || ''}`.toUpperCase();
  }

  private load(): void {
    this.isLoading = true;
    this.sommelierService
      .getClientSommelierByUser(this.userId)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: info => {
          this.info = info;
          this.revokeCurrentPhoto();
          if (info?.currentSommelierFileAttachmentId) {
            this.storageService.getImage(info.currentSommelierFileAttachmentId).subscribe({
              next: blob => (this.currentPhotoUrl = URL.createObjectURL(blob)),
              error: () => {},
            });
          }
        },
        error: () => (this.info = null),
      });
  }

  isSwitchingRole = false;

  /**
   * winepassport#348 (WP-002). Flip de rol de la cuenta a sommelier.
   * winepassport#387: se quitó la dirección inversa ("Convertir en cliente").
   * Cierra las sesiones activas del usuario (SecurityStamp) — de ahí el confirm explícito.
   */
  changeRole(targetRole: 'sommelier'): void {
    if (this.isSwitchingRole) return;
    const message = 'La cuenta pasará a ser sommelier y se cerrarán sus sesiones activas.';

    this.confirmation
      .confirm({ title: '::AreYouSure', message, type: 'danger', showCancelButton: true })
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.isSwitchingRole = true;
        this.roleSwitchService
          .changeUserRole({ userId: this.userId, targetRole })
          .pipe(finalize(() => (this.isSwitchingRole = false)))
          .subscribe(() => {
            this.not.success('', '::Success');
            this.load();
          });
      });
  }

  openModal(): void {
    this.showModal = true;
    this.keyword = '';
    this.province = null;
    this.locality = null;
    this.selected = null;
    this.search();
  }

  closeModal(): void {
    this.showModal = false;
  }

  onSearch(term: string): void {
    this.keyword = term;
    this.search();
  }

  onClearSearch(): void {
    this.keyword = '';
    this.search();
  }

  onFilter(): void {
    this.search();
  }

  search(): void {
    this.isSearching = true;
    this.searchError = false;
    this.catalogService
      .searchSommeliers({
        keyword: this.keyword || undefined,
        province: this.province || undefined,
        locality: this.locality || undefined,
        skipCount: 0,
        maxResultCount: 50,
      })
      .pipe(finalize(() => (this.isSearching = false)))
      .subscribe({
        next: res => {
          this.revokeResultPhotos();
          this.results = res.items ?? [];
          this.results.forEach(s => {
            if (s.fileAttachmentId) {
              this.storageService.getImage(s.fileAttachmentId).subscribe({
                next: blob => {
                  if (this.results.some(r => r.id === s.id)) {
                    this.resultPhotos[s.id] = URL.createObjectURL(blob);
                  }
                },
                error: () => {},
              });
            }
          });
          this.provinceOptions = this.distinct(this.results.map(r => r.region)).map(v => ({ value: v, label: v }));
          this.localityOptions = this.distinct(this.results.map(r => r.city)).map(v => ({ value: v, label: v }));
          if (this.selected && !this.results.some(r => r.id === this.selected!.id)) {
            this.selected = null;
          }
        },
        error: () => {
          this.searchError = true;
          this.results = [];
        },
      });
  }

  private distinct(values: (string | undefined)[]): string[] {
    return [...new Set(values.filter((v): v is string => !!v && v.trim().length > 0))].sort((a, b) =>
      a.localeCompare(b, 'es'),
    );
  }

  pick(s: SommelierPublicDto): void {
    this.selected = s;
  }

  confirm(): void {
    if (!this.selected || this.isSubmitting) return;

    this.isSubmitting = true;
    this.sommelierService
      .changeClientSommelierByUser({ userId: this.userId, sommelierId: this.selected.id })
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: () => {
          this.not.success('', '::MySommelier:Changed', [`${this.selected!.name} ${this.selected!.surname}`]);
          this.showModal = false;
          this.load();
        },
      });
  }
}
